import { allPieces, images, allMapsImageData } from "@/utils/data"
import classNames from "classnames"
import { mapDesigns } from "@mono/mapDesigns"
import Money from "@/Components/Money"
import { gameRoles } from "@/utils/gameRoles"
import Color from "color"
import { useState } from "react"
import ActionCard from "@/Components/ActionCard"
import { getPlayerAvatar } from "@/utils/playerAvatar"

const tempUserImage =
  "https://plus.unsplash.com/premium_photo-1671656349322-41de944d259b?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8dXNlcnxlbnwwfHwwfHx8MA%3D%3D"

interface HistoryPlayerItemProps {
  game: IGame
  player: IPlayer
  user: IUser
}

const HistoryPlayerItem: React.FC<HistoryPlayerItemProps> = ({ game, player, user }) => {
  const [showCardsModal, setShowCardsModal] = useState(false)
  const piece = allPieces.find((item) => item.id === player.pieceId)
  const avatar = getPlayerAvatar(player, user)
  const map = mapDesigns.find((map) => map.id === game.settings.mapId)
  const properties = player.properties.map(
    (propertyId) => map?.cells.find((cell) => cell.order === propertyId) as ICell
  )

  const mapImageData = allMapsImageData.find((imageData) => imageData.mapId === game.settings.mapId)

  // Find card details for each card
  const playerCards = player.cards
    .map((card) => {
      // card.cardId is the id of the card
      const cardData = map?.allChances?.find((c) => c.id === card.cardId)
      return cardData || undefined
    })
    .filter((c): c is IBonusCard => Boolean(c))

  return (
    <>
      <div className="grid grid-cols-[1fr_0.75fr] xl:grid-cols-[1fr_0.7fr] lg:grid-cols-[1fr] md:grid-cols-[auto_25rem] gap-x-2">
        {/* Player Info Section */}
        <div className="flex relative justify-between items-center py-1 md:min-h-[4rem] md:py-0 px-2 md:px-4 bg-trade-panelBackground">
          <div className="flex gap-4 items-center">
            <img className="md:h-9 md:w-9 h-7 w-7" src={piece?.imgSrc} alt="piece" />
            <div className="flex gap-2 items-center">
              <img
                className="h-9 w-9 rounded-full object-cover md:block hidden"
                src={user.profileImage ? encodeURI(user.profileImage) : avatar || tempUserImage}
                alt="profile"
              />
              <div className="flex flex-col gap-3">
                <div className="flex gap-2 items-center">
                  <div className="flex flex-col gap-2">
                    <div className="font-poppins text-white text-base">
                      {user.username}

                      {player.role !== "none" && (
                        <div
                          className="px-2 py-0.5 md:hidden flex rounded-md text-xs font-medium font-poppins h-fit whitespace-nowrap"
                          style={{
                            backgroundColor: gameRoles.find((r) => r.id === player.role)?.color + "40",
                            color: gameRoles.find((r) => r.id === player.role)?.color,
                          }}
                        >
                          {gameRoles.find((r) => r.id === player.role)?.title}
                        </div>
                      )}
                    </div>
                  </div>
                  {player.role !== "none" && (
                    <div
                      className="px-2 py-0.5 rounded-md text-xs font-medium font-poppins h-fit md:flex hidden"
                      style={{
                        backgroundColor: gameRoles.find((r) => r.id === player.role)?.color + "40",
                        color: gameRoles.find((r) => r.id === player.role)?.color,
                      }}
                    >
                      {gameRoles.find((r) => r.id === player.role)?.title}
                    </div>
                  )}

                  <div className="flex items-center gap-1">
                    <img
                      className="h-5 md:h-8 cursor-pointer"
                      src={images.icons.cards}
                      alt="cards"
                      onClick={() => setShowCardsModal(true)}
                    />
                    <span className="text-white text-sm font-medium">{player.cards.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Money and Stock Section */}
          <div className="flex flex-col items-end gap-1">
            {player.isBankrupt ? (
              <div className="text-xl text-white font-poppins font-bold">Bankrupt</div>
            ) : (
              <>
                <Money
                  money={player.money}
                  iconClass="h-1/2"
                  className="text-white text-2xl font-semibold font-poppins gap-1"
                />
                {player.stock && player.stock > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-300 text-sm">Stock:</span>
                    <Money money={player.stock} iconClass="h-3" className="text-green-400 text-sm font-medium gap-1" />
                    {player.stockMultiplier && player.stockMultiplier > 1 && (
                      <span className="text-yellow-400 text-xs">(×{player.stockMultiplier})</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Properties Section */}
        <div className="flex gap-4 gap-y-0 overflow-auto w-full p-3 h-full bg-[#d9d9d92e]">
          {properties.length > 0 ? (
            properties.map((property) => {
              const group = map?.groups.find((g) => g.id === property.propertyDetails?.groupId) as IPropertyGroup
              const imageData = mapImageData?.cellImages.find((cellImage) => cellImage.order === property.order)
              const myColor = new Color(group?.withStrip ? group.color : "white")
              const colorDark = myColor.darken(0.3)
              const colorLight = myColor.lighten(0.1)

              return (
                <div
                  key={property.order}
                  className="flex-shrink-0 border border-black shadow-lg"
                  style={{
                    filter: `drop-shadow(4px 4px 2px rgba(0, 0, 0, 0.25))`,
                    backgroundImage: `radial-gradient(circle at 25% 25%, ${colorLight.hex()} 0%, ${colorDark.hex()} 100%)`,
                    height: "32px",
                    width: "24px",
                  }}
                >
                  {imageData?.smallCardImage && (
                    <div className="w-full h-full p-[2px] py-[3px]">
                      <div className="border-black h-full w-full flex items-center justify-center">
                        <img src={imageData.smallCardImage} className="h-auto w-full" alt="property" />
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          ) : (
            <div className="text-gray-400 text-sm italic">No properties</div>
          )}
        </div>
      </div>

      {/* Modal for showing cards */}
      {showCardsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-gray-900 rounded-2xl p-8 max-w-lg h-[90vh] overflow-auto w-full border-2 border-gray-700 relative">
            <button
              className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300"
              onClick={() => setShowCardsModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="text-2xl font-bold text-white mb-6 text-center">{user.username}'s Cards</h3>
            {playerCards.length === 0 ? (
              <div className="text-gray-400 text-center">No cards</div>
            ) : (
              <div className="flex flex-wrap gap-4 justify-center">
                {playerCards.map((card, idx) => (
                  <ActionCard key={card.id || idx} card={card} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default HistoryPlayerItem
